import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Button,
    Grid,
    Card,
    CardContent,
    TextField,
    Container,
  } from "@mui/material";
  import GraphicEqIcon from "@mui/icons-material/GraphicEq";
  import BluetoothIcon from "@mui/icons-material/Bluetooth";
  import SettingsIcon from "@mui/icons-material/Settings";
  import DevicesIcon from "@mui/icons-material/Devices";
  import StarIcon from "@mui/icons-material/Star";
  import LocationOnIcon from "@mui/icons-material/LocationOn";
  import PhoneIcon from "@mui/icons-material/Phone";
  import TowerSpeaker from "./../images/TowerSpeaker.png";
  import SpeakerDriver from "./../images/speakerdriver.png";
  import mainImage from "./../images/main.png";
  import contactUs from "./../images/ContactUs.png";
  import { useState, useEffect, useRef } from "react";
  import { useLocation } from 'react-router-dom';
  import { Helmet } from 'react-helmet-async';
  
  function LandingPage() {
    const location = useLocation();
    const sectionRef = useRef(null);
    const imageRef = useRef(null);
    const [imageHeight, setImageHeight] = useState('auto');

    // Force title update on location change
    useEffect(() => {
      document.title = 'India Tech – Premium Multimedia Speakers for Home Entertainment';
    }, [location.pathname]);

    useEffect(() => {
      const calculateHeight = () => {
        if (sectionRef.current && window.innerWidth >= 900) {
          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            if (sectionRef.current) {
              const sectionHeight = sectionRef.current.offsetHeight;
              setImageHeight(`${sectionHeight + 100}px`);
            }
          });
        } else {
          setImageHeight('auto');
        }
      };

      // Calculate on mount with a small delay to ensure DOM is ready
      const timeoutId = setTimeout(calculateHeight, 100);

      // Recalculate on window resize
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(calculateHeight, 100);
      };
      window.addEventListener('resize', handleResize);

      // Use ResizeObserver for more accurate measurements
      const resizeObserver = new ResizeObserver(() => {
        calculateHeight();
      });

      if (sectionRef.current) {
        resizeObserver.observe(sectionRef.current);
      }

      return () => {
        clearTimeout(timeoutId);
        clearTimeout(resizeTimeout);
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
      };
    }, []);
    // Features data
    const features = [
      {
        icon: <GraphicEqIcon sx={{ fontSize: 40, color: "#E53935" }} />,
        title: "Rich Bass",
        description:
          "Offers solid sound output & ISO watts. Experience punchy bass with its powerful woofer.",
      },
      {
        icon: <BluetoothIcon sx={{ fontSize: 40, color: "#E53935" }} />,
        title: "Easy Connectivity",
        description: "Bluetooth, USB, FM Radio, AUX Cable & Mic.",
      },
      {
        icon: <SettingsIcon sx={{ fontSize: 40, color: "#E53935" }} />,
        title: "Easy Installation & Controls",
        description:
          "On-board button controls with all functionality you need.",
      },
      {
        icon: <DevicesIcon sx={{ fontSize: 40, color: "#E53935" }} />,
        title: "Dynamic Compatibility",
        description:
          "Connects to LED TVs, mobiles, laptops, PCs & DVD/CD players.",
      },
    ];
  
    // Testimonials data
    const testimonials = [
      {
        name: "Akash T.",
        review:
          "Best speaker purchase I made! Great sound and amazing RGB lights.",
      },
      {
        name: "Ravi K.",
        review:
          "Superb bass and connectivity. Really worth the money!",
      },
      {
        name: "Priya M.",
        review:
          "Looks elegant and sounds fantastic. Highly recommended.",
      },
      {
        name: "Sandeep L.",
        review:
          "Easy setup, powerful sound, and excellent compatibility.",
      },
    ];
  
    return (
      <>
        <Helmet key={location.pathname}>
          <title>India Tech – Premium Multimedia Speakers for Home Entertainment</title>
          <meta 
            name="description" 
            content="Discover India Tech's premium range of multimedia speakers built for powerful sound and immersive home entertainment. Experience modern design, deep bass, and superior audio performance." 
          />
          <meta property="og:title" content="India Tech – Premium Multimedia Speakers for Home Entertainment" />
          <meta property="og:description" content="Discover India Tech's premium range of multimedia speakers built for powerful sound and immersive home entertainment. Experience modern design, deep bass, and superior audio performance." />
          <meta property="twitter:title" content="India Tech – Premium Multimedia Speakers for Home Entertainment" />
          <meta property="twitter:description" content="Discover India Tech's premium range of multimedia speakers built for powerful sound and immersive home entertainment. Experience modern design, deep bass, and superior audio performance." />
        </Helmet>
        <Box>  
          {/* Hero Section */}
          <Box sx={{ backgroundColor: "#f4f4f4" }}>
          <Container sx={{ py: 8 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 6,
                alignItems: "center",
                minHeight: "600px"
              }}
            >
            {/* Left Section - Text Content */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%"
              }}
            >
              {/* Main Headline */}
              <Typography
                variant="h2"
                sx={{ 
                  fontWeight: "bold", 
                  lineHeight: 1.1,
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "3.5rem" }
                }}
              >
                <Box component="span" sx={{ color: "#000" }}>Saturn</Box>{" "}
                <Box component="span" sx={{ color: "#E53935" }}>Multimedia</Box>{" "}
                <Box component="span" sx={{ color: "#000" }}>Tower Speakers</Box>
              </Typography>
              
              {/* Description */}
              <Typography 
                sx={{ 
                  color: "black",
                  fontSize: "1.4rem",
                  lineHeight: 1.4,
                  mb: 4,
                  maxWidth: "500px"
                }}
              >
                Experience rich, professional sound at home with IndiaTech's
                flagship Saturn Multimedia Tower speakers. Elegant wood finish
                meets vibrant RGB LED technology.
              </Typography>
    
              {/* CTA Buttons */}
              <Box sx={{ display: "flex", gap: 3, mb: 6 }}>
                <Button
                  variant="contained"
                  sx={{ 
                    backgroundColor: "#c1ec4b",
                    color: "black",
                    px: 4,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    "&:hover": { 
                      backgroundColor: "#45a049" 
                    }
                  }}
                  endIcon={<Box sx={{ ml: 1 }}>→</Box>}
                >
                  Shop Now
                </Button>
                <Button 
                  variant="outlined" 
                  sx={{ 
                    color: "#333",
                    borderColor: "#ddd",
                    px: 4,
                    py: 2,
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    fontSize: "1.1rem",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#ccc"
                    }
                  }}
                  startIcon={<Box sx={{ mr: 1 }}>▶</Box>}
                >
                  Watch Demo
                </Button>
              </Box>
    
              {/* Key Features */}
              <Box sx={{ 
                display: "flex", 
                gap: 6,
                flexWrap: "wrap"
              }}>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ 
                    fontWeight: "800", 
                    fontSize: "1.8rem",
                    color: "black",
                    lineHeight: 1
                  }}>250W</Typography>
                  <Typography variant="body2" sx={{ 
                    color: "black",
                    fontSize: "0.9rem",
                    mt: 0.5
                  }}>
                    PMPO Power
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ 
                    fontWeight: "800", 
                    fontSize: "1.8rem",
                    color: "black",
                    lineHeight: 1
                  }}>RGB</Typography>
                  <Typography variant="body2" sx={{ 
                    color: "black",
                    fontSize: "0.9rem",
                    mt: 0.5
                  }}>
                    LED Effects
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "left" }}>
                  <Typography sx={{ 
                    fontWeight: "800", 
                    fontSize: "1.8rem",
                    color: "black",
                    lineHeight: 1
                  }}>USB</Typography>
                  <Typography variant="body2" sx={{ 
                    color: "black",
                    fontSize: "0.9rem",
                    mt: 0.5
                  }}>
                    Direct Play
                  </Typography>
                </Box>
              </Box>
            </Box>
    
            {/* Right Section - Product Image */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                height: "85%"
              }}
            > 
              {/* Main Product Image */}
              <Box
                component="img"
                src={mainImage}
                alt="Saturn Multimedia Tower Speaker"
                sx={{ 
                  width: "100%",
                  maxWidth: "500px",
                  height: "auto",
                  // borderRadius: 3,
                }}
              />
            </Box>
          </Box>
          </Container>
        </Box>
  
         {/* About Section */}
         <Container sx={{ py: 8 }}>
           <Box
             sx={{
               display: "grid",
               gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
               gap: 4,
               alignItems: "center",
               minHeight: "400px"
             }}
           >
             {/* Image Section */}
             <Box
               sx={{
                 display: "flex",
                 justifyContent: "center",
                 alignItems: "center",
                 width: "100%",
                 height: "400px",
                 borderRadius: 2,
                 position: "relative"
               }}
             >
               <Box
                 component="img"
                 src={SpeakerDriver}
                 alt="Speaker Driver"
                 sx={{
                   maxWidth: "100%",
                   maxHeight: "100%",
                   objectFit: "contain",
                   borderRadius: 1
                 }}
                 onError={(e) => {
                   console.log("Image failed to load:", e.target.src);
                   e.target.style.display = "none";
                 }}
               />
               <Typography
                 variant="body2"
                 sx={{
                   position: "absolute",
                   bottom: 8,
                   right: 8,
                   color: "#6c757d",
                   fontSize: "0.75rem"
                 }}
               >
               </Typography>
             </Box>

             {/* Text Section */}
             <Box sx={{ pl: { xs: 0, md: 2 } }}>
               <Typography
                 variant="h4"
                 sx={{ 
                   fontWeight: "bold", 
                   color: "#E53935",
                   mb: 2,
                   fontSize: { xs: "1.8rem", md: "3.2rem" }
                 }}
               >
                 About Us
               </Typography>
               <Typography sx={{ 
                 color: "black",
                 fontSize: "1.4rem",
                 lineHeight: 1.4,
                 mb: 3
               }}>
                 Professional audio quality meets stunning LED effects in our
                 flagship multimedia tower speakers.
               </Typography>

               <Box
                 sx={{
                   display: "grid",
                   gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" },
                   gap: 2
                 }}
               >
                 <Box sx={{ textAlign: "center", p: 2 }}>
                   <Typography sx={{ 
                     fontWeight: "800", 
                     fontSize: "2rem",
                     color: "black",
                     lineHeight: 1
                   }}>250+</Typography>
                   <Typography variant="body2" sx={{ 
                     color: "black",
                     fontSize: "0.9rem",
                     mt: 0.5
                   }}>PMPO Power</Typography>
                 </Box>
                 <Box sx={{ textAlign: "center", p: 2 }}>
                   <Typography sx={{ 
                     fontWeight: "800", 
                     fontSize: "2rem",
                     color: "black",
                     lineHeight: 1
                   }}>300+</Typography>
                   <Typography variant="body2" sx={{ 
                     color: "black",
                     fontSize: "0.9rem",
                     mt: 0.5
                   }}>LED Effects</Typography>
                 </Box>
                 <Box sx={{ textAlign: "center", p: 2 }}>
                   <Typography sx={{ 
                     fontWeight: "800", 
                     fontSize: "2rem",
                     color: "black",
                     lineHeight: 1
                   }}>20+</Typography>
                   <Typography variant="body2" sx={{ 
                     color: "black",
                     fontSize: "0.9rem",
                     mt: 0.5
                   }}>Direct Play</Typography>
                 </Box>
                 <Box sx={{ textAlign: "center", p: 2 }}>
                   <Typography sx={{ 
                     fontWeight: "800", 
                     fontSize: "2rem",
                     color: "black",
                     lineHeight: 1
                   }}>240+</Typography>
                   <Typography variant="body2" sx={{ 
                     color: "black",
                     fontSize: "0.9rem",
                     mt: 0.5
                   }}>Direct Play</Typography>
                 </Box>
               </Box>
             </Box>
           </Box>
         </Container>
  
        {/* Why Choose Section */}
        <Box id="features-section" sx={{ backgroundColor: "#f4f4f4" }}>
          <Container maxWidth="lg" sx={{ py: 8, paddingBottom: '179px' }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: '#E74C3C',
                  mb: 1,
                  fontSize: { xs: '1.8rem', md: '3.2rem' },
                  lineHeight: 1.2,
                }}
              >
                Why Choose Saturn Series?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'black',
                  fontSize: '1.4rem',
                  lineHeight: 1.4,
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Professional audio quality meets stunning LED effects in our flagship multimedia tower speakers
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3,
                maxWidth: '1200px',
                mx: 'auto',
              }}
            >
              {features.map((feature, index) => (
                <Card
                  key={index}
                  sx={{
                    backgroundColor: '#c1ec4b',
                    color: '#333',
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 2,
                    height: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {feature.icon}
                    </Box>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: '800',
                      mb: 0.25,
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      color: '#00',
                      minHeight: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#000", mb: 4,
                      lineHeight: 1.1,
                      fontSize: '0.85rem',
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 !important',
                      padding: '0 !important',
                      '&.MuiTypography-root': {
                        margin: '0 !important',
                        padding: '0 !important',
                        lineHeight: '1.1 !important',
                        flex: 'none'
                      },
                      '&.MuiTypography-body2': {
                        margin: '0 !important',
                        padding: '0 !important',
                        lineHeight: '1.1 !important',
                        flex: 'none'
                      }
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Container>
        </Box>
  
        {/* Transform Section */}
        
        <Box 
          ref={sectionRef}
          sx={{ 
            py: 0, 
            backgroundColor: "#c1ec4b",
            position: 'relative'
          }}
        >
          <Container sx={{ py: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: 4 }}>
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left',maxWidth: '50% !important' } }}>
                <Typography
                  variant="h4"
                  sx={{ 
                    fontWeight: "bold", 
                    mb: 3,
                    color: "#000",
                    fontSize: { xs: '1.8rem', md: '3.2rem' }
                  }}
                >
                  Ready to Transform Your Entertainment?
                </Typography>
                <Typography sx={{ color: "#000", mb: 4, fontSize: '1.4rem', lineHeight: 1.4 }}>
                  Join thousands of satisfied customers who've upgraded their home
                  audio experience with IndiaTech.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    sx={{ 
                      backgroundColor: "#000", 
                      color: "#fff",
                      px: 4,
                      py: 1.5,
                      "&:hover": { bgcolor: "#333" }
                    }}
                  >
                    Explore Features
                  </Button>
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      backgroundColor: "transparent",
                      color: "#000", 
                      borderColor: "#000",
                      px: 4,
                      py: 1.5,
                      "&:hover": { 
                        backgroundColor: "transparent",
                        borderColor: "#000"
                      }
                    }}
                  >
                    Get Quote
                  </Button>
                </Box>
              </Box>
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: { xs: 'center', md: 'flex-end' },
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: 0 },
                bottom: { xs: 'auto', md: 0 },
                width: { xs: '100%', md: '50%' },
                height: { xs: 'auto', md: '100%' }
              }}>
                <Box
                  ref={imageRef}
                  component="img"
                  src={TowerSpeaker}
                  alt="Speaker Tower"
                  sx={{ 
                    width: 'auto',
                    maxWidth: { xs: '300px', sm: '400px', md: '500px' },
                    height: { xs: 'auto', md: imageHeight },
                    maxHeight: { xs: '300px', sm: '400px', md: imageHeight },
                    objectFit: 'contain',
                    objectPosition: 'bottom',
                    zIndex: 2,
                  }}
                />
              </Box>
            </Box>
          </Container>
        </Box>
  
        {/* Testimonials */}
        <Box sx={{ py: 8, px: { xs: 2, md: 8 }, textAlign: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#E53935", fontSize: { xs: '1.8rem', md: '3.2rem' } }}>
            Customer testimonials
          </Typography>
          <Typography sx={{ mt: 1, mb: 4, color: "black", fontSize: '1.4rem', lineHeight: 1.4 }}>
            Professional audio quality meets stunning LED effects in our flagship
            multimedia tower speakers.
          </Typography>
  
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 2,
              maxWidth: '1200px',
              mx: 'auto',
            }}
          >
            {testimonials.map((item, idx) => (
              <Card
                key={idx}
                sx={{
                  backgroundColor: "#c1ec4b",
                  borderRadius: 2,
                  boxShadow: 2,
                  height: "100%",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 4 }}>
                  <Typography sx={{ mb: 2, color: "black", fontSize: '1.1rem', lineHeight: 1.4 }}>
                    "{item.review}"
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: "black" }} />
                    ))}
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#E53935" }}>
                    {item.name}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
  
         {/* Contact Section */}
         <Box id="contact-us-section" 
           sx={{ 
             py: 8, 
             position: "relative",
             minHeight: "600px",
             backgroundImage: `url(${contactUs})`,
             backgroundSize: "cover",
             backgroundPosition: "center",
             backgroundRepeat: "no-repeat",
             "&::before": {
               content: '""',
               position: "absolute",
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               backgroundColor: "rgba(0, 0, 0, 0.3)",
               zIndex: 1
             }
           }}
         >
           <Container sx={{ position: "relative", zIndex: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                alignItems: "center",
                minHeight: "500px"
              }}
            >
              {/* Contact Form */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                 <Card
                   sx={{
                     p: 4,
                     backgroundColor: "rgba(255, 255, 255, 0.95)",
                     backdropFilter: "blur(10px)",
                     borderRadius: 3,
                     boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                     border: "1px solid rgba(255, 255, 255, 0.2)",
                     maxWidth: "600px",
                     width: "100%"
                   }}
                 >
                   <Typography 
                     variant="h4" 
                     sx={{ 
                       fontWeight: "bold", 
                       mb: 1,
                       color: "#333",
                       fontSize: { xs: "1.8rem", md: "2.2rem" }
                     }}
                   >
                     Contact Us
                   </Typography>
                   <Typography 
                     sx={{ 
                       mb: 3, 
                       color: "#666",
                       fontSize: "1rem"
                     }}
                   >
                     We'll get back to you as soon as possible
                   </Typography>
                   
                   <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                     {/* Name and Phone Row */}
                     <Box sx={{ display: "flex", gap: 2 }}>
                       <TextField 
                         fullWidth 
                         placeholder="Enter Your Name*"
                         variant="outlined"
                         sx={{
                           "& .MuiOutlinedInput-root": {
                             backgroundColor: "#fff",
                             borderRadius: 2
                           }
                         }}
                       />
                       <TextField 
                         fullWidth 
                         placeholder="Enter Phone No.*"
                         variant="outlined"
                         sx={{
                           "& .MuiOutlinedInput-root": {
                             backgroundColor: "#fff",
                             borderRadius: 2
                           }
                         }}
                       />
                     </Box>
                     
                     {/* Email Field */}
                     <TextField 
                       fullWidth 
                       placeholder="Enter Email Address*"
                       variant="outlined"
                       sx={{
                         "& .MuiOutlinedInput-root": {
                           backgroundColor: "#fff",
                           borderRadius: 2
                         }
                       }}
                     />
                     
                     {/* Message Field */}
                     <TextField 
                       fullWidth 
                       placeholder="Enter Your Message"
                       multiline 
                       rows={3} 
                       variant="outlined"
                       sx={{
                         "& .MuiOutlinedInput-root": {
                           backgroundColor: "#fff",
                           borderRadius: 2
                         }
                       }}
                     />
                     
                     {/* Buttons Row */}
                     <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                       <Button
                         variant="contained"
                         sx={{ 
                           backgroundColor: "#c1ec4b", 
                           color: "black",
                           px: 4,
                           py: 1.5,
                           borderRadius: 2,
                           fontWeight: "bold",
                           "&:hover": { 
                             backgroundColor: "#45a049" 
                           }
                         }}
                         endIcon={<Box sx={{ ml: 1 }}>→</Box>}
                       >
                         Enquire Now
                       </Button>
                       <Button
                         variant="outlined"
                         sx={{ 
                           color: "#333",
                           borderColor: "#ddd",
                           px: 4,
                           py: 1.5,
                           borderRadius: 2,
                           backgroundColor: "#fff",
                           "&:hover": {
                             backgroundColor: "#f5f5f5",
                             borderColor: "#ccc"
                           }
                         }}
                         startIcon={<Box sx={{ mr: 1 }}>▶</Box>}
                       >
                         Watch Demo
                       </Button>
                     </Box>
                                        {/* Shop Information */}
                   <Box 
                     sx={{ 
                       mb: 4,
                       p: 2,
                       backgroundColor: "#f8f9fa",
                       borderRadius: 2,
                       border: "1px solid #e9ecef"
                     }}
                   >
                     <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                       <LocationOnIcon sx={{ color: "#E53935", fontSize: 24, mt: 0.5 }} />
                       <Box>
                         <Typography sx={{ fontWeight: "bold", mb: 0.5, color: "#333", fontSize: "0.9rem" }}>
                           Address
                         </Typography>
                         <Typography sx={{ color: "#666", lineHeight: 1.5, fontSize: "0.9rem" }}>
                         HARPREET ELECTRONICS
                         <br />
                         H-271 SECTOR 3 DSIDC
                         <br />
                         BAWANA INDUSTRIAL AREA DELHI
                         </Typography>
                       </Box>
                     </Box>
                     
                     <Box sx={{ display: "flex", gap: 2 }}>
                       <PhoneIcon sx={{ color: "#E53935", fontSize: 24, mt: 0.5 }} />
                       <Box>
                         <Typography sx={{ fontWeight: "bold", mb: 0.5, color: "#333", fontSize: "0.9rem" }}>
                           Phone Number
                         </Typography>
                         <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
                           +91 8799711313
                         </Typography>
                       </Box>
                     </Box>
                   </Box>
                   </Box>
                 </Card>
               </Box>
             </Box>
           </Container>
         </Box>
  
        {/* Footer */}
        <Box sx={{ py: 3, backgroundColor: "#111", color: "#fff", textAlign: "center" }}>
          <Typography variant="body2">
            ©2025. All rights reserved. IndiaTech Website Designed & Managed by Honcho Metrics
          </Typography>
        </Box>
      </Box>
      </>
    );
  }
  
  export default LandingPage;
  